import { objectToQueryParams } from "../../util/logic";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";

// Many of these types are copied from the src/notifications/* files
// in purchaser-api.
export enum NotificationType {
    // Requisitions
    REQUISITION_ASSIGNED = "requisition:assigned",
    REQUISITION_MESSAGE_RECEIVED = "requisition:message_received",
    REQUISITION_NEW = "requisition:new",
    // RFQs
    RFQ_BROADCAST_SENT = "rfq:broadcast_sent",
    RFQ_NO_QUOTE_RECEIVED = "rfq:no_quote_received",
    RFQ_QUOTE_RECEIVED = "rfq:quote_received",
    RFQ_STATUS_CHANGED = "rfq:status_changed",
    RFQ_VENDOR_ADDED = "rfq:vendor_added",
    RFQ_VENDOR_RESPONSE_RECEIVED = "rfq:vendor_response_received",
}

export type NotificationEvent = {
    type: NotificationType;
    title: string;
    subtitle: string;
    content?: string;
    attachmentCount?: number;
    timestamp?: number;
    // TODO: Might need to recreate the types from the API
    rfqId: number;
    rfqUniquePublicId: string;
    userId: number;
    vendorRelationshipId: number;
    mailboxId: number;
    rfqQuoteId: number;
    comparisonId: number;
    requisitionId: string;
    requisitionMessageId: string;
};

export type Notification = {
    id: number;
    isRead: boolean;
    createdAt: string;
    userId: number;
    event: NotificationEvent;
};

export type NotificationsFilterParams = {
    cursor?: number;
    pageSize?: number;
};

export enum NotificationChannel {
    IN_APP = "IN_APP",
    EMAIL = "EMAIL",
}

export type RequisitionPreferenceFieldKey = "requisitionAssigned" | "requisitionMessageReceived" | "requisitionNew";

export type RfqPreferenceFieldKey =
    | "rfqBroadcastSent"
    | "rfqNoQuoteReceived"
    | "rfqQuoteReceived"
    | "rfqStatusChanged"
    | "rfqVendorAdded"
    | "rfqVendorResponseReceived";

export type PreferenceFieldKey = RequisitionPreferenceFieldKey | RfqPreferenceFieldKey;

export const notificationSubscriptionMap: Record<NotificationType, PreferenceFieldKey> = {
    // Requisitions
    "requisition:assigned": "requisitionAssigned",
    "requisition:message_received": "requisitionMessageReceived",
    "requisition:new": "requisitionNew",
    // RFQs
    "rfq:broadcast_sent": "rfqBroadcastSent",
    "rfq:no_quote_received": "rfqNoQuoteReceived",
    "rfq:quote_received": "rfqQuoteReceived",
    "rfq:status_changed": "rfqStatusChanged",
    "rfq:vendor_added": "rfqVendorAdded",
    "rfq:vendor_response_received": "rfqVendorResponseReceived",
};

export type UserRfqNotificationPreferences = {
    id: number;
    createdAt: string;
    updatedAt: string;
    userId: number;

    // Preferences are defined here as a boolean-like option that
    // specifies which notification channels. Any preferences with
    // an empty array are "turned off".
    // This is duplicated from the API Prisma schema and should be
    // kept in sync.
    rfqNoQuoteReceived: NotificationChannel[];
    rfqOutboundCommunicationSent: NotificationChannel[];
    rfqQuoteReceived: NotificationChannel[];
    rfqStatusChanged: NotificationChannel[];
    rfqVendorAdded: NotificationChannel[];
    rfqVendorResponseReceived: NotificationChannel[];
};

export type UserRequisitionNotificationPreferences = {
    id: number;
    createdAt: string;
    updatedAt: string;
    userId: number;

    // Preferences are defined here as a boolean-like option that
    // specifies which notification channels. Any preferences with
    // an empty array are "turned off".
    // This is duplicated from the API Prisma schema and should be
    // kept in sync.
    requisitionAssigned: NotificationChannel[];
    requisitionMessageReceived: NotificationChannel[];
    requisitionNew: NotificationChannel[];
};

export default {
    getNotifications: async (params?: NotificationsFilterParams) => {
        const queryParams = params ? objectToQueryParams(params) : null;
        const queryString = queryParams ? "?" + queryParams.toString() : "";

        return fetch(`${PURCHASER_API_BASE_URL}/notifications${queryString}`, {
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Error fetching notifications");
            }
            return (await res.json()) as Notification[];
        });
    },
    markAsRead: (notificationId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/notifications/${notificationId}/mark-as-read`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error marking notification as read");
            }
            return res.json();
        }),
    markAsUnread: (notificationId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/notifications/${notificationId}/mark-as-unread`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error marking notification as unread");
            }
            return res.json();
        }),
    markAllAsRead: (notificationIds: number[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/notifications/mark-all-as-read`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ notificationIds }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error marking all notifications as read");
            }
            return res.json();
        }),
    deleteNotification: (notificationId: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/notifications/${notificationId}`, {
            method: "DELETE",
            headers: getHeaders(),
            body: JSON.stringify({}),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error deleting notification");
            }
            return res.json();
        }),
    getRfqAssignmentNotificationPreferences: () =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/rfq-assignment`, {
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Error fetching rfq-assignment notification preferences");
            }
            return (await res.json()) as UserRfqNotificationPreferences;
        }),
    getRfqSubscriptionNotificationPreferences: () =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/rfq-subscription`, {
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Error fetching rfq-subscription notification preferences");
            }
            return (await res.json()) as UserRfqNotificationPreferences;
        }),
    getRequisitionsNotificationPreferences: () =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/requisitions`, {
            headers: getHeaders(),
        }).then(async res => {
            if (!res.ok) {
                throw new Error("Error fetching requisitions notification preferences");
            }
            return (await res.json()) as UserRequisitionNotificationPreferences;
        }),
    updateRfqAssignmentNotificationPreference: (type: PreferenceFieldKey, channels: NotificationChannel[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/rfq-assignment`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ type, channels }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error updating rfq-assignment notification preference");
            }
            return res.json();
        }),
    updateRfqSubscriptionNotificationPreference: (type: PreferenceFieldKey, channels: NotificationChannel[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/rfq-subscription`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ type, channels }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error updating rfq-subscription notification preference");
            }
            return res.json();
        }),
    updateRequisitionsNotificationPreference: (type: PreferenceFieldKey, channels: NotificationChannel[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/notification-preferences/requisitions`, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ type, channels }),
        }).then(res => {
            if (!res.ok) {
                throw new Error("Error updating requisitions notification preference");
            }
            return res.json();
        }),
};
